import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const UsageContext = createContext(null);

/** Returns {used, limit, loading, refresh, percent}. `refresh()` re-pulls from
 *  the server; everything that bumps the counter (e.g. the Generate button in
 *  Draft Creator) can call it so the chip updates immediately. */
export function useUsage() {
  const ctx = useContext(UsageContext);
  if (!ctx) throw new Error("useUsage must be used inside <UsageProvider>");
  return ctx;
}

/** Lightweight provider — one HTTP call on mount + whenever a child calls
 *  refresh(). Deliberately not polling: AI generations are user-initiated,
 *  and the Generate button in DraftCreator triggers a refresh locally. */
export function UsageProvider({ children }) {
  const [state, setState] = useState({ used: 0, limit: 15, month: null, plan: "free", loading: true });

  const refresh = useCallback(async (cached) => {
    try {
      const { data } = await api.get("/usage/current-month", cached === true ? { swr: true } : { fresh: true });
      setState({
        used: Number(data?.used) || 0,
        limit: data?.limit ?? null,
        month: data?.month || null,
        plan: data?.plan || "free",
        trialEndsAt: data?.trial_ends_at || null,
        loading: false,
      });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, []);

  useEffect(() => { refresh(true); }, [refresh]);

  const value = useMemo(() => {
    const percent = state.limit > 0 ? Math.min(100, Math.round((state.used / state.limit) * 100)) : 0;
    const isPaid = state.plan === "pro";
    const isTrial = state.plan === "trial";
    const isStarter = state.plan === "starter";
    const isResearch = state.plan === "research";
    const isPro = isPaid || isTrial || isResearch;
    const trialDaysLeft = isTrial && state.trialEndsAt
      ? Math.max(1, Math.ceil((new Date(state.trialEndsAt) - Date.now()) / 86400000))
      : 0;
    const nearLimit = !isPro && percent >= 80 && percent < 100;
    const overLimit = !isPro && percent >= 100;
    return { ...state, isPro, isPaid, isTrial, isStarter, trialDaysLeft, percent, nearLimit, overLimit, refresh };
  }, [state, refresh]);

  return <UsageContext.Provider value={value}>{children}</UsageContext.Provider>;
}
